import { useTranslation } from "react-i18next";
import PageWrapper from "../PageWrapper/PageWrapper";
import 'react-datepicker/dist/react-datepicker.css';
import { useCallback, useState } from "react";
import { Contract } from "../../api/ContractsAPI";
import ContractTemplateEditor from "../../components/ContractTemplateEditor/ContractTemplateEditor";
import ContractDisplay from "../../components/ContractDisplay/ContractDisplay";
import { PermissionFlags } from "../../components/PermissionFlags/PermissionFlags";
import Bubbles from "../../components/Bubbles/Bubbles";
import { useContractTemplate } from "../../hooks/useContractTemplate";
import debounce from "../../shared/debounce";
import SelectInput from "../../components/SelectInput/SelectInput";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../components/SecondaryButton/SecondaryButton";
import { CardV2, CardContent, CardHeader, CardTitle } from "../../components/CardV2/CardV2";
import { FileText, Globe } from "lucide-react";
import ContractsAPI from "../../api/ContractsAPI";
import { toastService } from "../../services/ToastService";

const EMPTY_CONTRACT: Contract = {
  signatureDay: new Date().toISOString(), // YYYY-MM-DDTHH:mm:ss.sssZ
  clientFullname: '',
  clientPersonalID: '',
  clientAddress: '',
  clientPhoneNumber: '',
  clientEmail: '',
  dress: '',
  dressTrain: '',
  dressVeil: '',
  dressVeilLength: 0,
  dressDescription: '',
  dressLegCut: '',
  weddingDate: new Date().toISOString(), // YYYY-MM-DDTHH:mm:ss.sssZ
  bust: '',
  waist: '',
  hips: '',
  totalPrice: 0,
  totalPriceWording: '',
  totalPriceCurrency: '',
  instalments: [],
  endDate: new Date().toISOString(), // YYYY-MM-DDTHH:mm:ss.sssZ
  clientPickUpDate: new Date().toISOString(), // YYYY-MM-DDTHH:mm:ss.sssZ
  employeeName: '',
  language: 'PL_PL',
  firstFollowUpMeetingDate: new Date().toISOString(), // YYYY-MM-DDTHH:mm:ss.sssZ
}

function ContractsPage() {

  const { t } = useTranslation();
  const [contract, setContract] = useState<Contract>(EMPTY_CONTRACT);
  const { contractTemplate, updateContractTemplate, setLocale } = useContractTemplate();
  const [isEditing, setIsEditing] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handleLanguageChange = (newLanguage: string) => {
    setLocale(newLanguage);
    setContract(prev => ({...prev, language: newLanguage }));
    toastService.showToast(t("contracts.language_changed") || `Language changed to ${newLanguage}`, "success");
  }

  const handleGenerateTestPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const testContract: Contract = {
        ...EMPTY_CONTRACT,
        language: contract.language,
        clientFullname: 'Test Client',
        clientPersonalID: 'TEST123456',
        clientAddress: '123 Test Street, Test City',
        clientPhoneNumber: '+1234567890',
        clientEmail: 'test@example.com',
        dress: 'Test Dress',
        dressTrain: 'Yes',
        dressVeil: 'Yes',
        dressVeilLength: 50,
        dressDescription: 'Beautiful test dress for demonstration',
        dressLegCut: 'Standard',
        weddingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        bust: '90cm',
        waist: '70cm',
        hips: '95cm',
        totalPrice: 1500,
        totalPriceWording: 'One thousand five hundred',
        totalPriceCurrency: 'EUR',
        instalments: [
          { amount: 500, dueDate: new Date().toISOString(), method: 'Card', amountInWords: 'Five hundred' },
          { amount: 1000, dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(), method: 'Transfer', amountInWords: 'One thousand' }
        ],
        endDate: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString(), // 25 days from now
        clientPickUpDate: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString(), // 28 days from now
        employeeName: 'Test Employee',
        firstFollowUpMeetingDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
      };

      const response = await ContractsAPI.preview(testContract);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      
      window.open(url, "_blank");
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `test-contract-${contract.language.toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      toastService.showToast(t("contracts.test_pdf_success") || "Test contract PDF generated successfully!", "success");
    } catch (error) {
      console.error('Error generating test PDF:', error);
      toastService.showToast(t("contracts.test_pdf_error") || "Failed to generate test contract PDF", "error");
    } finally {
      setIsGeneratingPDF(false);
    }
  }

  const handleToggleEditing = () => {
    setIsEditing(!isEditing);
    if (isEditing) {
      toastService.showToast(t("contract_template.editing_disabled") || "Template editing disabled", "success");
    } else {
      toastService.showToast(t("contract_template.editing_enabled") || "Template editing enabled", "success");
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdate = useCallback(
    debounce(updateContractTemplate, 500),
    [updateContractTemplate]
  );
  
  return (
    <PageWrapper>
      <>
        <Bubbles count={10} />
        <CardV2 className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {t("contract_template.title") || "Contract Template"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {t("contracts.language") || "Language"}
                </label>
                <SelectInput 
                  values={[
                    { value: 'EN_US', readableValue: 'global.english'},
                    { value: 'PL_PL', readableValue: 'global.polish'},
                    { value: 'PT_PT', readableValue: 'global.portuguese'},
                    { value: 'ES_ES', readableValue: 'global.spanish'},
                    { value: 'IT_IT', readableValue: 'global.italian'},
                    { value: 'DE_DE', readableValue: 'global.german'},
                  ]}
                  onChange={e => handleLanguageChange(e.target.value)}
                  label=""
                />
              </div>

              <div className="space-y-2">
                <PermissionFlags requiredFlags={['edit-contract-template']}>
                  <PrimaryButton
                    text={isEditing ? (t("contract_template.button.revert") || "See Contract") : (t("contract_template.button") || "Edit Contract Template")}
                    fn={handleToggleEditing}
                    actionName={isEditing ? "Revert Template Edit" : "Edit Template"}
                    isLoading={false}
                  />
                </PermissionFlags>
              </div>

              <div className="space-y-2 justify-self-end">
                <SecondaryButton
                  text={
                    <span className="flex items-center">
                      {isGeneratingPDF 
                        ? (t("contracts.generating") || "Generating...") 
                        : (t("contracts.generate_test") || "Generate Test PDF")}
                    </span>
                  }
                  fn={handleGenerateTestPDF}
                  actionName="Generate Test Contract PDF"
                  icon={isGeneratingPDF ? undefined : <FileText className="w-4 h-4" />}
                  iconPosition="left"
                />
              </div>
            </div>
          </CardContent>
        </CardV2>

        {isEditing ? (
          <ContractTemplateEditor contractTemplate={contractTemplate} updateContractTemplate={debouncedUpdate}/>
        ) : (
          <ContractDisplay contract={contract} template={contractTemplate} setContract={setContract} reset={() => setContract(EMPTY_CONTRACT)}/>
        )}
      </>
    </PageWrapper>
  );
}

export default ContractsPage;
